import React from 'react';
import { useTranslation } from 'react-i18next';

import { Skeleton } from '@/nova/components';
import { ACQ, STATUS_COLORS } from '@/nova/theme/tokens';
import { formatMoneyValue, parseDay } from '../../reports-helpers';
import type { AffiliateCommissionsReport } from '../../types';
import {
  HeroPanel,
  HeroHead,
  HeroLabel,
  ProvisionalChip,
  HeroValue,
  SplitBar,
  SplitSeg,
  SplitLegend,
  LegendItem,
  Swatch,
  LegendValue,
} from './dashboard.styles';

export interface CommissionPanelProps {
  commissions?: AffiliateCommissionsReport;
  /** The `YYYY-MM` the commissions figure covers (month of the range end). */
  month: string;
  currency: string;
  loading: boolean;
}

const share = (part: number, total: number): number => (total > 0 ? (part / total) * 100 : 50);

/**
 * The hero Total Commission panel: the month's commission with its CPA vs Rev Share
 * split. Month-scoped (commissions are monthly on the API), so it names the month and
 * flags an open period as provisional, keeping it honest for non-month ranges.
 */
export const CommissionPanel = ({ commissions, month, currency, loading }: CommissionPanelProps): React.ReactElement => {
  const { t, i18n } = useTranslation();
  const totals = commissions?.totals;
  const commission = totals?.commission_usd ?? 0;
  const cpa = totals?.cpa_usd ?? 0;
  const rev = totals?.revshare_usd ?? 0;
  const total = cpa + rev;
  const provisional = commissions?.period_status === 'open';

  const monthLabel = React.useMemo(() => {
    try {
      return new Intl.DateTimeFormat(i18n.language || 'en', { month: 'long', year: 'numeric' }).format(
        parseDay(`${month}-01`),
      );
    } catch {
      return month;
    }
  }, [month, i18n.language]);

  return (
    <HeroPanel>
      <HeroHead>
        <HeroLabel text={t('Total Commission')} tag="span" />
        {provisional ? <ProvisionalChip text={t('Provisional')} tag="span" /> : null}
      </HeroHead>

      {loading ? (
        <Skeleton width="60%" height={34} radius={8} />
      ) : (
        <HeroValue>{formatMoneyValue(commission, currency)}</HeroValue>
      )}

      <SplitBar aria-hidden="true">
        {total > 0 ? (
          <>
            <SplitSeg $share={share(cpa, total)} $tone="cpa" />
            <SplitSeg $share={share(rev, total)} $tone="rev" />
          </>
        ) : null}
      </SplitBar>

      <SplitLegend>
        <LegendItem>
          <Swatch $color={ACQ.orange} />
          {t('CPA')}
          <LegendValue>{formatMoneyValue(cpa, currency)}</LegendValue>
        </LegendItem>
        <LegendItem>
          <Swatch $color={STATUS_COLORS.positive} />
          {t('Rev Share')}
          <LegendValue>{formatMoneyValue(rev, currency)}</LegendValue>
        </LegendItem>
      </SplitLegend>

      <LegendItem>{t('{{month}} commission', { month: monthLabel })}</LegendItem>
    </HeroPanel>
  );
};

export default CommissionPanel;
